import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';

const Dropdown = ({ options = [], value, onChange, placeholder, style, icon, searchable = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 0 });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        (!menuRef.current || !menuRef.current.contains(event.target))
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = (e) => {
      // Don't close if the scroll is happening inside the dropdown menu itself
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (isOpen) setIsOpen(false);
    };
    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const actualMenuHeight = Math.min(options.length * 40 + (searchable ? 50 : 0) + 8, 400);
      
      let topPos = rect.bottom;
      if (spaceBelow < actualMenuHeight && spaceAbove > spaceBelow) {
        topPos = rect.top - actualMenuHeight;
      }
      
      setCoords({ left: rect.left, top: topPos, width: rect.width });
      
      if (searchable && inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isOpen, options.length, searchable]);

  const selectedOption = options.find(opt => opt.value === value) || null;
  
  const filteredOptions = searchable 
    ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        className="input-field flex items-center justify-between"
        style={{ 
          width: '100%', 
          backgroundColor: 'white', 
          cursor: 'pointer',
          paddingRight: '1rem',
          borderRadius: '12px'
        }}
        onClick={() => { setIsOpen(!isOpen); setSearchTerm(''); }}
      >
        <div className="flex items-center gap-2">
          {icon}
          <div style={{ color: !selectedOption && placeholder ? 'var(--color-text-muted)' : 'var(--color-text-main)', display: 'flex', alignItems: 'center', textAlign: 'left' }}>
            {selectedOption ? (selectedOption.render ? selectedOption.render() : selectedOption.label) : placeholder}
          </div>
        </div>
        <ChevronDown 
          size={16} 
          color="var(--color-text-muted)" 
          style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} 
        />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            zIndex: 9999,
            maxHeight: '400px',
            overflowY: 'auto',
            animation: 'fadeIn 0.15s ease-out'
          }}
          className="hide-scrollbar"
        >
          {searchable && (
            <div style={{ padding: '0.5rem', borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} color="var(--color-text-muted)" style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input 
                  ref={inputRef}
                  type="text" 
                  placeholder="Search..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', padding: '0.4rem 0.4rem 0.4rem 1.75rem', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '0.85rem' }}
                />
              </div>
            </div>
          )}
          
          {filteredOptions.length > 0 ? filteredOptions.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm('');
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '0.75rem 1.125rem',
                fontSize: '0.9rem',
                cursor: 'pointer',
                backgroundColor: value === option.value ? 'var(--color-primary-light)' : 'transparent',
                color: value === option.value ? 'var(--color-primary)' : 'var(--color-text-main)',
                fontWeight: value === option.value ? '600' : '400',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => { if (value !== option.value) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
              onMouseLeave={(e) => { if (value !== option.value) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {option.render ? option.render() : option.label}
            </div>
          )) : (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
              No results found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Dropdown;
